// @flow
import { type AbstractComponent } from 'react';
import { typography, color, FONT_WEIGHTS, SPACING } from '@tablekit/theme';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Button from '@tablekit/button';
import Icon from '@tablekit/icon';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import LazyLoad from 'react-lazyload';
import { LAZY_LOAD_OFFSET } from 'utils/theme';
import type { BlokComponentPropsType, CardImageType } from 'components/types';
import CoreImage from 'components/Image';
import { Card } from '../styled';

const GUTTERS = {
  min576: `${parseFloat(SPACING.L3) / 2}px`,
  min1024: `${parseFloat(SPACING.L4) / 2}px`,
  min1280: `${parseFloat(SPACING.L5) / 2}px`
};

export const CardImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: stretch;

  @media (min-width: 576px) {
    margin: 0 -${GUTTERS.min576};
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  @media (min-width: 1024px) {
    justify-content: ${({ totalCards }) =>
      totalCards === 3 ? 'space-between' : 'center'};
    margin: 0 -${GUTTERS.min1024};
  }

  @media (min-width: 1280px) {
    margin: 0 -${GUTTERS.min1280};
  }

  @media (min-width: 1440px) {
    justify-content: space-around;
    flex-wrap: nowrap;
  }
`;

export const ImageCard = styled(Card)`
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  align-self: stretch;
  display: flex;
  flex: 1 1 100%;
  padding-bottom: ${SPACING.L2};
  margin-bottom: ${SPACING.L5};

  @media (min-width: 576px) {
    ${({ totalCards }) => {
      if (totalCards === 4) {
        return css`
          margin: 0 ${SPACING.L3} ${SPACING.L6};
          max-width: calc(50% - ${SPACING.L3} - ${SPACING.L3});
        `;
      }
      return css`
        margin: 0 ${GUTTERS.min576} ${SPACING.L4};
        max-width: calc(33.33% - ${GUTTERS.min576} - ${GUTTERS.min576});
      `;
      // This semi-colon must be here or emotion merges media queries together
    }};
  }

  @media (min-width: 1024px) {
    margin: 0 ${GUTTERS.min1024} ${SPACING.L6};
    max-width: 248px;
    ${({ totalCards }) => {
      if (totalCards === 4) {
        return css`
          max-width: calc(25% - ${GUTTERS.min1024} - ${GUTTERS.min1024});
        `;
      }
      return css`
        width: calc(33.33% - ${GUTTERS.min1024} - ${GUTTERS.min1024});
      `;
    }};
  }

  @media (min-width: 1280px) {
    margin: 0 ${GUTTERS.min1280} ${SPACING.L6};
    max-width: 248px;
    ${({ totalCards }) => {
      if (totalCards === 4) {
        return css`
          max-width: calc(25% - ${GUTTERS.min1280} - ${GUTTERS.min1280});
        `;
      }
      return css`
        width: calc(33.33% - ${GUTTERS.min1280} - ${GUTTERS.min1280});
      `;
    }};
  }

  @media (min-width: 1440px) {
    margin: 0 ${GUTTERS.min1280} ${SPACING.L6};
    max-width: 248px;
    ${({ totalCards }) => {
      const width = 100 / totalCards;
      return css`
        width: calc(${width}% - ${GUTTERS.min1280} - ${GUTTERS.min1280});
      `;
    }};
  }
`;

export const ErrorCard = styled(ImageCard)`
  background-color: ${color('danger')};
  color: white;
  justify-content: center;
  align-items: center;
  text-align: center;
  ${typography('L6')};
  font-weight: ${FONT_WEIGHTS.BOLD};
  padding: ${SPACING.L4};
`;

export const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${SPACING.L4} ${SPACING.L4} 0;

  @media (min-width: 361px) {
    flex-direction: column;
  }
`;

export const imageDimensions = {
  small: 80,
  regular: 120
};

const Image = styled(CoreImage)`
  display: flex;
  max-width: ${imageDimensions.small}px;
  width: ${imageDimensions.small}px;
  max-height: ${imageDimensions.small}px;

  @media (min-width: ${({ regularSizeBreakpoint }) =>
      regularSizeBreakpoint}px) {
    max-width: ${imageDimensions.regular}px;
    height: ${imageDimensions.regular}px;
    width: auto;
    max-height: ${imageDimensions.regular}px;
  }
`;

export const Text = styled.div`
  ${typography('L4')};
  margin-left: ${SPACING.L4};
  max-width: 100%;
  display: flex;
  text-align: left;
  flex: 1 1 100%;

  @media (min-width: 361px) {
    margin-left: 0;
    margin-top: ${SPACING.L4};
    text-align: center;
  }
`;

export const LinkButton = styled(Button)`
  align-self: flex-end;

  @media (min-width: 361px) {
    align-self: center;
    & > span > span:last-of-type {
      display: none;
    }
    & > span > span:first-of-type {
      margin: 0;
    }
  }
`;

export function CardImage({
  blok: { image, link, link_text: linkText, title },
  styles,
  totalCards,
  component: Component = ImageCard,
  ...others
}: BlokComponentPropsType<CardImageType> & {
  component: AbstractComponent<*, *>,
  styles: any,
  totalCards: number
}) {
  let regularSizeBreakpoint = totalCards === 4 ? 361 : 577;
  if (Component !== ImageCard) {
    regularSizeBreakpoint = 768;
  }
  return (
    <Component link={link} totalCards={totalCards} {...others}>
      <TopRow>
        <LazyLoad offset={LAZY_LOAD_OFFSET}>
          <Image
            src={image}
            alt={title || linkText || image}
            regularSizeBreakpoint={regularSizeBreakpoint}
            options={[
              {
                breakpoint: `(min-width: ${regularSizeBreakpoint}px)`,
                options: {
                  size: {
                    height: imageDimensions.regular,
                    width: imageDimensions.regular
                  }
                }
              },
              {
                options: {
                  size: {
                    height: imageDimensions.small,
                    width: imageDimensions.small
                  }
                }
              }
            ]}
          />
        </LazyLoad>
        <Text>{title}</Text>
      </TopRow>
      <LinkButton
        appearance="subtle"
        iconAfter={<Icon icon={faChevronRight} />}
      >
        {linkText}
      </LinkButton>
    </Component>
  );
}
